/**
 * Maintenance Window Form Component
 *
 * Modal form for creating or editing maintenance windows.
 * Supports weekly recurring and one-time windows with timezone settings.
 *
 * @module components/SLA/MaintenanceWindowForm
 */

import React, { useState, useEffect } from 'react';
import { MaintenanceWindow, RecurrenceType } from '@shared/types/sla';
import slaService from '../../services/slaService';
import { Modal, Button } from '../ui';

interface Client {
  client_id: string;
  client_name: string;
}

interface MaintenanceWindowFormProps {
  /** Existing window to edit (undefined for create) */
  window?: MaintenanceWindow;
  /** Callback when window is successfully created/updated */
  onWindowChanged: () => void;
  /** Callback to close the modal */
  onClose: () => void;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Common timezones (subset)
const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Australia/Sydney',
];

/**
 * Maintenance Window Form - Create/edit maintenance windows
 */
const MaintenanceWindowForm: React.FC<MaintenanceWindowFormProps> = ({
  window: existingWindow,
  onWindowChanged,
  onClose,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(existingWindow?.client_id || '');
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(
    existingWindow?.recurrence_type || 'weekly'
  );
  const [dayOfWeek, setDayOfWeek] = useState<number | ''>(existingWindow?.day_of_week ?? '');
  const [startTime, setStartTime] = useState(existingWindow?.start_time || '');
  const [endTime, setEndTime] = useState(existingWindow?.end_time || '');
  const [specificDate, setSpecificDate] = useState(existingWindow?.specific_date || '');
  const [startDatetime, setStartDatetime] = useState(existingWindow?.specific_start_datetime || '');
  const [endDatetime, setEndDatetime] = useState(existingWindow?.specific_end_datetime || '');
  const [timezone, setTimezone] = useState(existingWindow?.timezone || 'UTC');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    // Mock clients - replace with actual API call
    setClients([
      { client_id: 'client-1', client_name: 'Client A' },
      { client_id: 'client-2', client_name: 'Client B' },
    ]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      const data: any = {
        client_id: selectedClientId,
        recurrence_type: recurrenceType,
        timezone,
      };

      if (recurrenceType === 'weekly') {
        if (dayOfWeek === '') {
          setError('Day of week is required for weekly windows');
          setLoading(false);
          return;
        }
        data.day_of_week = dayOfWeek;
        data.start_time = startTime;
        data.end_time = endTime;
      } else {
        data.specific_date = specificDate || undefined;
        data.specific_start_datetime = startDatetime;
        data.specific_end_datetime = endDatetime;
      }

      if (existingWindow) {
        await slaService.updateMaintenanceWindow(existingWindow.id, data);
      } else {
        await slaService.createMaintenanceWindow(data);
      }

      onWindowChanged();
    } catch (err) {
      console.error('Failed to save maintenance window:', err);
      setError(err instanceof Error ? err.message : 'Failed to save maintenance window');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size='md'
      title={existingWindow ? 'Edit Maintenance Window' : 'New Maintenance Window'}
      footer={
        <>
          <Button type='button' variant='outline' onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type='submit' form='maintenance-window-form' loading={loading} disabled={loading}>
            {loading ? 'Saving...' : existingWindow ? 'Update Window' : 'Create Window'}
          </Button>
        </>
      }
    >
      <p className='text-sm text-[var(--color-text-secondary)]'>
        Schedule a recurring or one-time maintenance window. Downtime during these windows
        is excluded from SLA calculations.
      </p>

      {error && (
        <div className='mt-4 rounded-md border border-[var(--color-status-danger)] bg-[var(--color-status-danger)]/10 p-4'>
          <div className='text-sm text-[var(--color-status-danger)]'>{error}</div>
        </div>
      )}

      <form id='maintenance-window-form' onSubmit={handleSubmit} className='mt-6 space-y-4'>
        {/* Client Selection */}
        <div>
          <label htmlFor='client' className='block text-sm font-medium text-[var(--color-text-primary)]'>
            Client *
          </label>
          <select
            id='client'
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            required
            disabled={!!existingWindow}
            className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] disabled:opacity-50 sm:text-sm'
          >
            <option value=''>Select a client</option>
            {clients.map((client) => (
              <option key={client.client_id} value={client.client_id}>
                {client.client_name}
              </option>
            ))}
          </select>
        </div>

        {/* Recurrence Type */}
        <div>
          <label className='block text-sm font-medium text-[var(--color-text-primary)]'>Recurrence Type *</label>
          <div className='mt-2 space-x-4'>
            <label className='inline-flex items-center'>
              <input
                type='radio'
                value='weekly'
                checked={recurrenceType === 'weekly'}
                onChange={(e) => setRecurrenceType(e.target.value as RecurrenceType)}
                className='form-radio h-4 w-4 text-[var(--color-accent)]'
              />
              <span className='ml-2 text-sm text-[var(--color-text-primary)]'>Weekly Recurring</span>
            </label>
            <label className='inline-flex items-center'>
              <input
                type='radio'
                value='one-time'
                checked={recurrenceType === 'one-time'}
                onChange={(e) => setRecurrenceType(e.target.value as RecurrenceType)}
                className='form-radio h-4 w-4 text-[var(--color-accent)]'
              />
              <span className='ml-2 text-sm text-[var(--color-text-primary)]'>One-Time</span>
            </label>
          </div>
        </div>

        {/* Conditional Fields - Weekly */}
        {recurrenceType === 'weekly' && (
          <div className='space-y-4 border-l-4 border-[var(--color-accent)] pl-4'>
            <div className='grid grid-cols-3 gap-4'>
              <div>
                <label
                  htmlFor='dayOfWeek'
                  className='block text-sm font-medium text-[var(--color-text-primary)]'
                >
                  Day of Week *
                </label>
                <select
                  id='dayOfWeek'
                  value={dayOfWeek}
                  onChange={(e) =>
                    setDayOfWeek(e.target.value ? parseInt(e.target.value) : '')
                  }
                  required
                  className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
                >
                  <option value=''>Select day</option>
                  {DAYS_OF_WEEK.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor='startTime'
                  className='block text-sm font-medium text-[var(--color-text-primary)]'
                >
                  Start Time *
                </label>
                <input
                  type='time'
                  id='startTime'
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
                />
              </div>
              <div>
                <label htmlFor='endTime' className='block text-sm font-medium text-[var(--color-text-primary)]'>
                  End Time *
                </label>
                <input
                  type='time'
                  id='endTime'
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
                />
              </div>
            </div>
          </div>
        )}

        {/* Conditional Fields - One-Time */}
        {recurrenceType === 'one-time' && (
          <div className='space-y-4 border-l-4 border-[var(--color-status-warning)] pl-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label
                  htmlFor='startDatetime'
                  className='block text-sm font-medium text-[var(--color-text-primary)]'
                >
                  Start Date & Time *
                </label>
                <input
                  type='datetime-local'
                  id='startDatetime'
                  value={startDatetime}
                  onChange={(e) => setStartDatetime(e.target.value)}
                  required
                  className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
                />
              </div>
              <div>
                <label
                  htmlFor='endDatetime'
                  className='block text-sm font-medium text-[var(--color-text-primary)]'
                >
                  End Date & Time *
                </label>
                <input
                  type='datetime-local'
                  id='endDatetime'
                  value={endDatetime}
                  onChange={(e) => setEndDatetime(e.target.value)}
                  required
                  className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
                />
              </div>
            </div>
          </div>
        )}

        {/* Timezone */}
        <div>
          <label htmlFor='timezone' className='block text-sm font-medium text-[var(--color-text-primary)]'>
            Timezone (Optional)
          </label>
          <select
            id='timezone'
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className='mt-1 block w-full rounded-md border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] shadow-sm focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)] sm:text-sm'
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
            Defaults to client's timezone if not specified
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default MaintenanceWindowForm;
export { MaintenanceWindowForm };
